import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnDestroy,
  signal
} from '@angular/core';
import { AbstractControl } from '@angular/forms';
import {
  Subject,
  Subscription,
  catchError,
  from,
  map,
  of,
  switchMap
} from 'rxjs';

import { SSO_ERROR_PARAM, SsoService } from '../../services/sso.service';
import { ssoService as viveSsoService } from '../../shared/vive-api/sso.service';

@Component({
  selector: 'app-sso-login-button',
  templateUrl: './sso-login-button.component.html',
  styleUrls: ['./sso-login-button.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class SsoLoginButtonComponent implements OnDestroy {
  @Input({ required: true }) public emailControl!: AbstractControl;
  // False where a sign-in cannot finish through SSO, e.g. the vendor signup
  // page: SSO connections are staff or client only.
  @Input() public enabled = true;

  private readonly discovered = signal<{
    email: string;
    connectionId: number;
  } | null>(null);
  private readonly emailCommits = new Subject<string>();
  private readonly subscription: Subscription;
  private lastRequestedEmail: string | null = null;

  constructor(private ssoService: SsoService) {
    // switchMap drops a slower answer for an email the user has moved on from.
    this.subscription = this.emailCommits
      .pipe(
        switchMap((email) =>
          from(
            viveSsoService.discover({
              data: { email },
              options: {
                context: { SkipLoadingMask: true, ManuallyHandleError: true }
              }
            })
          ).pipe(
            // Silent either way: a failure (throttled, offline) leaves password
            // sign-in as the only option until the next ask.
            catchError(() => of(null)),
            map((discovery) => ({ email, discovery }))
          )
        )
      )
      .subscribe(({ email, discovery }) => {
        if (discovery === null) {
          // A failed lookup is not an answer: forget it so the next blur or
          // submit asks again.
          this.lastRequestedEmail = null;
          this.discovered.set(null);
          return;
        }
        this.discovered.set(
          discovery.sso && discovery.connectionId !== undefined
            ? { email, connectionId: discovery.connectionId }
            : null
        );
      });
  }

  /** The host form calls this on email blur and on submit. */
  public discover(): void {
    const email = this.normalizedEmail();
    if (
      !this.enabled ||
      this.emailControl.invalid ||
      !email ||
      email === this.lastRequestedEmail
    ) {
      return;
    }
    this.lastRequestedEmail = email;
    this.emailCommits.next(email);
  }

  public showButton(): boolean {
    const discovered = this.discovered();
    // Hidden as soon as the field no longer holds the email that was discovered.
    // The field value is not a signal: an edit re-renders this only because the
    // component uses Default change detection under the host form's view. Under
    // OnPush the button would outlive the edit and start() would use the old email.
    return discovered !== null && discovered.email === this.normalizedEmail();
  }

  public start(): void {
    const discovered = this.discovered();
    if (!discovered) {
      return;
    }
    this.ssoService.clearError();
    // Back to this page, minus a code from an earlier attempt and the hash.
    const returnTo = new URL(window.location.href);
    returnTo.searchParams.delete(SSO_ERROR_PARAM);
    returnTo.hash = '';
    window.location.assign(
      viveSsoService.startUrl({
        connectionId: discovered.connectionId,
        returnTo: returnTo.toString(),
        loginHint: discovered.email
      })
    );
  }

  public ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private normalizedEmail(): string {
    const value: unknown = this.emailControl.value;
    return typeof value === 'string' ? value.trim().toLowerCase() : '';
  }
}
